import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiSellerDiscount } from '@wix/da-papi-types';
import {
  BiData,
  BiContextProvidedValue,
  ViewAvailableDiscountsClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import DiscountIcon from '@wix/da-ds/pkg/Icons/16x16/Discount';
import DiscountsPopup from '../DiscountsPopup';
import { useRestrictToLoggedIn } from '../utils/hooks/useRestrictToLoggedIn';

import s from './DiscountsPopupTrigger.scss';

interface Props {
  className?: string;
  allDiscounts: PapiSellerDiscount[];
  eligibleDiscount?: PapiSellerDiscount;
}

const DiscountsPopupTrigger: React.FC<Props> = ({
  className,
  allDiscounts = [],
  eligibleDiscount,
}) => {
  const { isLoggedIn, restrictToLoggedIn } = useRestrictToLoggedIn();
  const [showDiscountsPopup, setShowDiscountsPopup] = useState(false);
  const { t } = useTranslation();

  // Don't show if there are no discounts
  if (!allDiscounts?.length) {
    return null;
  }

  // Don't show if user has eligible discount(s) AND there's only 1 total discount
  // (nothing else for them to see)
  if (eligibleDiscount && allDiscounts.length === 1) {
    return null;
  }

  const hasEligibleDiscount = eligibleDiscount !== undefined;
  const discountIds: number[] = allDiscounts.map(d => d.discountId);

  return (
    <div className={cn(s['root'], className)}>
      <TextButton
        key="save"
        size="medium"
        variant="main"
        priority="secondary"
        className={s['btn-show-discounts']}
        onClick={restrictToLoggedIn(ReferralType.AVAILABLE_DISCOUNTS, () =>
          setShowDiscountsPopup(true)
        )}
        icon={hasEligibleDiscount ? undefined : DiscountIcon}
        iconClassName={s['icon-discount']}
        biData={BiData<ViewAvailableDiscountsClickBiEvent>({
          evid: 457,
          sectionname: BiContextProvidedValue,
          typeid: BiContextProvidedValue,
          itemid: BiContextProvidedValue,
          campaignid_list: discountIds.join(','),
        })}
      >
        {hasEligibleDiscount
          ? t('popup.discounts.trigger.viewAll')
          : t('popup.discounts.trigger.viewAvailable')}
      </TextButton>
      {isLoggedIn && (
        <DiscountsPopup
          isOpen={showDiscountsPopup}
          onRequestClose={() => setShowDiscountsPopup(false)}
          discounts={allDiscounts}
        />
      )}
    </div>
  );
};
DiscountsPopupTrigger.displayName = 'DiscountsPopupTrigger';

export default DiscountsPopupTrigger;
